import {inject, Injectable} from "@angular/core"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {
    CreateDataObjectThumbnailsGQL,
    CreateTextureSetRevisionThumbnailsGQL,
    CreateTypedDataObjectSetThumbnailsGQL,
} from "@common/services/generate-thumbnail/generate-thumbnail.generated"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {ThumbnailOptionsInput, TypedDataObjectInput} from "@generated"

@Injectable({
    providedIn: "root",
})
export class GenerateThumbnailService {
    private readonly organization = inject(OrganizationsService)
    private readonly createDataObjectThumbnailsGql = inject(CreateDataObjectThumbnailsGQL)
    private readonly createTypedDataObjectSetThumbnailsGql = inject(CreateTypedDataObjectSetThumbnailsGQL)
    private readonly createTextureSetRevisionThumbnailsGql = inject(CreateTextureSetRevisionThumbnailsGQL)

    public async createThumbnailForDataObject(dataObjectId: number | string, options?: ThumbnailOptionsInput, name?: string, organizationId?: string) {
        const resolvedOrganizationId = organizationId ? await this.organization.ensureUserIsMember(organizationId) : undefined
        return mutateThrowingErrors(this.createDataObjectThumbnailsGql)({
            dataObjectId: typeof dataObjectId === "string" ? dataObjectId : undefined,
            dataObjectLegacyId: typeof dataObjectId === "number" ? dataObjectId : undefined,
            name,
            options,
            organizationId: resolvedOrganizationId,
        }).then(({createDataObjectThumbnails}) => createDataObjectThumbnails)
    }

    public async createThumbnailForDataObjectSet(
        typedDataObjectSet: TypedDataObjectInput[],
        options?: ThumbnailOptionsInput,
        name?: string,
        organizationId?: string,
    ) {
        const resolvedOrganizationId = await this.organization.ensureUserIsMember(organizationId)
        return mutateThrowingErrors(this.createTypedDataObjectSetThumbnailsGql)({
            typedDataObjectSet,
            name,
            options,
            organizationId: resolvedOrganizationId,
        }).then(({createTypedDataObjectSetThumbnails}) => createTypedDataObjectSetThumbnails)
    }

    public async createThumbnailForTextureSetRevision(textureSetRevisionId: string, options?: ThumbnailOptionsInput, name?: string, organizationId?: string) {
        const resolvedOrganizationId = organizationId ? await this.organization.ensureUserIsMember(organizationId) : undefined
        return mutateThrowingErrors(this.createTextureSetRevisionThumbnailsGql)({
            textureSetRevisionId,
            name,
            options,
            organizationId: resolvedOrganizationId,
        }).then(({createTextureSetRevisionThumbnails}) => createTextureSetRevisionThumbnails)
    }
}
